/**
 * Typography component using design system typography tokens
 * This provides consistent typography across the web app
 */

import React from 'react';
import { clsx } from 'clsx';

// Typography variant types based on design system
export type TypographyVariant = 
  | 'heading1'
  | 'heading2' 
  | 'heading3'
  | 'heading4'
  | 'bodyLarge'
  | 'bodyMedium'
  | 'bodySmall'
  | 'caption'
  | 'label'
  | 'wineName'
  | 'wineVarietal'
  | 'wineVintage'
  | 'venueName'
  | 'venueLocation'
  | 'reviewTitle'
  | 'reviewBody'
  | 'reviewRating';

export type TypographyElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div' | 'label';

interface TypographyProps {
  variant: TypographyVariant;
  children: React.ReactNode;
  as?: TypographyElement;
  className?: string;
}

// Map variants to Tailwind classes based on design system tokens.
// Editorial display moments (h1/h2, wineName, venueName, reviewTitle)
// use font-serif (Young Serif — 400-only, so font-normal everywhere serif
// is used to avoid browser-synthesized bold). UI chrome, metadata,
// labels, and long-form body use font-sans (Geist). See .impeccable.md.
const variantClasses: Record<TypographyVariant, string> = {
  // General content typography — display headings serif, smaller headings sans
  heading1: 'font-serif text-4xl font-normal leading-tight',
  heading2: 'font-serif text-3xl font-normal leading-tight',
  heading3: 'font-sans text-2xl font-semibold leading-tight',
  heading4: 'font-sans text-xl font-semibold leading-tight',
  bodyLarge: 'font-sans text-lg font-normal leading-relaxed',
  bodyMedium: 'font-sans text-base font-normal leading-normal',
  bodySmall: 'font-sans text-sm font-normal leading-normal',
  caption: 'font-sans text-xs font-normal leading-normal tracking-wide',
  label: 'font-sans text-sm font-medium leading-normal tracking-wide uppercase',

  // Wine-specific typography — editorial name in serif, data/metadata in sans
  wineName: 'font-serif text-xl font-normal leading-tight',
  wineVarietal: 'font-sans text-lg font-medium leading-normal tracking-wide',
  wineVintage: 'font-sans text-sm font-medium leading-normal tracking-widest uppercase',

  // Venue-specific typography — editorial name in serif, location meta in sans
  venueName: 'font-serif text-2xl font-normal leading-tight',
  venueLocation: 'font-sans text-base font-normal leading-normal tracking-wide',

  // Review typography — editorial title in serif, body in sans (Young Serif
  // is a display face; long-form reading wants Geist).
  reviewTitle: 'font-serif text-lg font-normal leading-tight',
  reviewBody: 'font-sans text-base font-normal leading-relaxed',
  reviewRating: 'font-sans text-sm font-medium leading-normal tracking-widest',
};

// Default HTML elements for each variant
const defaultElements: Record<TypographyVariant, TypographyElement> = {
  heading1: 'h1',
  heading2: 'h2',
  heading3: 'h3',
  heading4: 'h4',
  bodyLarge: 'p',
  bodyMedium: 'p',
  bodySmall: 'p',
  caption: 'span',
  label: 'label',
  wineName: 'h3',
  wineVarietal: 'h4',
  wineVintage: 'span',
  venueName: 'h2',
  venueLocation: 'p',
  reviewTitle: 'h3',
  reviewBody: 'p',
  reviewRating: 'span',
};

export const Typography: React.FC<TypographyProps> = ({
  variant,
  children,
  as,
  className,
}) => {
  const Component = as || defaultElements[variant];
  
  return (
    <Component
      className={clsx(
        variantClasses[variant],
        className
      )}
    >
      {children}
    </Component>
  );
};

// Convenience components for common use cases
export const Heading1: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="heading1" {...props} />
);

export const Heading2: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="heading2" {...props} />
);

export const Heading3: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="heading3" {...props} />
);

export const Heading4: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="heading4" {...props} />
);

export const BodyText: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="bodyMedium" {...props} />
);

export const Caption: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="caption" {...props} />
);

export const Label: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="label" {...props} />
);